import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, Search, ClipboardList, History, Pencil, Trash2 } from "lucide-react";
import { fmtDateOnly as fmtDate, fmtDateTime as fmtTs } from "@/lib/datetime";
import { formatApiError } from "@/lib/api";
import RecordFormDialog from "@/components/RecordFormDialog";
import ConfirmDeleteDialog from "@/components/ConfirmDeleteDialog";

function RecordDetailDialog({ recordId, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && recordId) {
      setLoading(true);
      api
        .get(`/admin/records/${recordId}`)
        .then((r) => setData(r.data))
        .catch(() => toast.error("Failed to load record"))
        .finally(() => setLoading(false));
    } else {
      setData(null);
    }
  }, [open, recordId]);

  const r = data?.record;
  const logs = data?.edit_history || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Record detail</DialogTitle>
          <DialogDescription>Full daily record and its edit history.</DialogDescription>
        </DialogHeader>
        {loading || !r ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-foreground">{r.student_name}</p>
                <p className="text-sm text-muted-foreground">{fmtDate(r.date)} · recorded by {r.teacher_name}</p>
              </div>
              <Badge variant="secondary" className="font-normal">{r.points} pts</Badge>
            </div>

            <div className="grid grid-cols-1 gap-2 text-sm bg-muted/40 rounded-lg p-4">
              <Detail label="Memorization" value={r.memorization || "—"} />
              <Detail label="Review" value={r.review || "—"} />
              <Detail label="Other notes" value={r.other_notes || "—"} />
              <Detail label="Points" value={String(r.points)} />
            </div>

            {/* Edit history */}
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-foreground mb-2">
                <History className="h-4 w-4" /> Edit history
              </div>
              {logs.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="edit-history-empty">No edits recorded for this record.</p>
              ) : (
                <div className="border border-border rounded-lg overflow-hidden overflow-x-auto" data-testid="edit-history-table">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Field</TableHead>
                        <TableHead>Old</TableHead>
                        <TableHead>New</TableHead>
                        <TableHead>By</TableHead>
                        <TableHead>When</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {logs.map((l, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium whitespace-nowrap">{l.field_changed}</TableCell>
                          <TableCell className="text-muted-foreground">{l.old_value ?? "—"}</TableCell>
                          <TableCell>{l.new_value ?? "—"}</TableCell>
                          <TableCell className="text-muted-foreground whitespace-nowrap">{l.edited_by_name}</TableCell>
                          <TableCell className="text-muted-foreground whitespace-nowrap">{fmtTs(l.edited_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Detail({ label, value, note }) {
  return (
    <div>
      <span className="text-muted-foreground">{label}: </span>
      <span className="text-foreground">{value}</span>
      {note ? <span className="text-muted-foreground"> — {note}</span> : null}
    </div>
  );
}

export default function AllRecords() {
  const [records, setRecords] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nameSearch, setNameSearch] = useState("");
  const [teacherId, setTeacherId] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [editRecord, setEditRecord] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/records/${deleteTarget.id}`);
      toast.success("Record deleted");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  useEffect(() => {
    api.get("/admin/teachers").then((r) => setTeachers(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (teacherId !== "all") params.teacher_id = teacherId;
      if (start) params.start = start;
      if (end) params.end = end;
      const { data } = await api.get("/admin/records", { params });
      setRecords(data);
    } catch (e) {
      toast.error("Failed to load records");
    } finally {
      setLoading(false);
    }
  }, [teacherId, start, end]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const filtered = useMemo(
    () => records.filter((r) => r.student_name.toLowerCase().includes(nameSearch.toLowerCase())),
    [records, nameSearch]
  );

  return (
    <div className="max-w-6xl" data-testid="all-records-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-foreground">All Records</h1>
      <p className="mt-1 text-muted-foreground">Every daily record across the school.</p>

      {/* Filters */}
      <div className="mt-6 bg-card border border-border rounded-lg p-4 flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Student name</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input className="h-11 pl-9 w-52" placeholder="Search…" data-testid="records-name-search" value={nameSearch} onChange={(e) => setNameSearch(e.target.value)} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Teacher</Label>
          <Select value={teacherId} onValueChange={setTeacherId}>
            <SelectTrigger className="h-11 w-48" data-testid="records-teacher-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All teachers</SelectItem>
              {teachers.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.full_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">From</Label>
          <Input type="date" className="h-11" data-testid="records-start" value={start} onChange={(e) => setStart(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">To</Label>
          <Input type="date" className="h-11" data-testid="records-end" value={end} onChange={(e) => setEnd(e.target.value)} />
        </div>
        {(nameSearch || teacherId !== "all" || start || end) && (
          <Button variant="ghost" className="h-11" data-testid="records-clear" onClick={() => { setNameSearch(""); setTeacherId("all"); setStart(""); setEnd(""); }}>
            Clear
          </Button>
        )}
      </div>

      <div className="mt-4 bg-card border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground" data-testid="records-empty">
            <ClipboardList className="h-8 w-8 mx-auto mb-3 opacity-40" /> No records match these filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Student</TableHead>
                  <TableHead>Teacher</TableHead>
                  <TableHead>Memorization</TableHead>
                  <TableHead>Points</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.id} className="cursor-pointer" onClick={() => setDetailId(r.id)} data-testid={`record-row-${r.id}`}>
                    <TableCell className="whitespace-nowrap">{fmtDate(r.date)}</TableCell>
                    <TableCell className="font-medium">{r.student_name}</TableCell>
                    <TableCell className="text-muted-foreground">{r.teacher_name}</TableCell>
                    <TableCell className="text-muted-foreground max-w-xs truncate">{r.memorization || "—"}</TableCell>
                    <TableCell>{r.points}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2"
                        onClick={(e) => { e.stopPropagation(); setEditRecord(r); }}
                        data-testid={`edit-record-${r.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-destructive hover:text-destructive"
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(r); }}
                        data-testid={`delete-record-${r.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <RecordDetailDialog recordId={detailId} open={!!detailId} onOpenChange={(v) => !v && setDetailId(null)} />

      <RecordFormDialog
        open={!!editRecord}
        onOpenChange={(v) => !v && setEditRecord(null)}
        studentId={editRecord?.student_id}
        record={editRecord}
        admin
        teachers={teachers}
        defaultTeacherId={editRecord?.teacher_id || ""}
        onSaved={load}
      />

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Delete record?"
        impact={deleteTarget ? `Delete the ${fmtDate(deleteTarget.date)} record for ${deleteTarget.student_name}? Its edit history will also be removed. This cannot be undone.` : ""}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
